/**
 * Enable parts of an element to be replaced with newly-rendered content from Section Rendering API
 *
 * ** Note! Dynamic elements MUST have a data-section-id attribute set on the custom element in Liquid **
 */
class DynamicSectionElement extends HTMLElement {
  constructor() {
    super();

    this.setSectionRenderLinkListeners();
  }

  /**
   *  Return the id of the section this custom element is rendered in
   */
  get sectionId() {
    return this.dataset.sectionId;
  }

  /**
   * Set event listeners for section render links
   * @returns {void}
   */
  setSectionRenderLinkListeners() {
    const links = this.querySelectorAll('[data-section-render-link]');
    if (links.length === 0) return;

    for (const link of links) {
      link.addEventListener('click', event => {
        event.preventDefault();
        this.onSectionRenderLinkClick(link.href);
      });
    }
  }

  async onSectionRenderLinkClick(href) {
    await this.loadSectionFromUrl(href, {
      replaceState: true
    });

    // The links must have a data-slot attribute on them, or a parent node.
    // This ensures they will be re-rendered when the section is re-rendered.
    // After this, we need to re-set the event listeners.
    this.setSectionRenderLinkListeners();
  }

  /**
   * Re-render the section for this module using a specified target URL (e.g. pagination or filter link)
   * @param {String} url
   * @param {Object} options
   * @returns {Promise{Response}}
   */
  async loadSectionFromUrl(url = '', options = {}) {
    // Replace current URL in browser history if required
    if (options.replaceState) {
      globalThis.history.replaceState(undefined, undefined, url);
    }

    // Load new HTML for this module's section from Section Rendering API
    const symbol = url.includes('?') ? '&' : '?';
    return fetch(`${url}${symbol}sections=${this.sectionId}`)
      .then(response => response.json())
      .then(responseJson => {
        const newSectionElement = new DOMParser().parseFromString(
          responseJson[this.sectionId],
          'text/html'
        );

        this.replaceContent(newSectionElement, this.sectionId, options);
      });
  }

  /**
   * Replace content marked with "data-slot" attributes inside current element
   * @param {HTMLElement} newSectionElement - The updated HTML Element for this section returned by the Section Rendering API
   * @param {Object} options
   */
  replaceContent(newSectionElement, options = {}) {
    // We are passed the entire section, so we need to find this custom element within the HTML
    const newCustomElement = newSectionElement.querySelector(
      `[data-section-id="${this.sectionId}"]`
    );

    // Sometimes we replace the entire section
    if (this.dataset.slot || options.replaceEntireSection) {
      this.replaceWith(newCustomElement.innerHTML);
      this.loadScripts();
      this.afterSectionRender();
      return;
    }

    // If not replacing the entire section, we find all descendant elements with data-slot attribute and update each with new contents
    const oldSlots = this.querySelectorAll('[data-slot]');
    for (const oldSlotElement of oldSlots) {
      const slotName = oldSlotElement.dataset.slot;
      const newElementSlot = newCustomElement.querySelector(
        `[data-slot="${slotName}"]`
      );

      try {
        oldSlotElement.replaceWith(newElementSlot);
      } catch {
        globalThis.PxlApp.isDev &&
          // eslint-disable-next-line no-console
          console.error(
            `Failed to update slots.
             - Section ID: ${this.sectionId}
             - SlotName: ${slotName}
             - OldSlotEl: ${oldSlotElement}
             - newCustomElement: ${newCustomElement}
             - newElSlot: ${newElementSlot}`
          );
      }
    }

    this.loadScripts();

    this.afterSectionRender();
  }

  /**
   * Override this method in child classes to run code after the section has been updated
   */
  afterSectionRender() {}

  /**
   * Re-inject scripts contained within the updated module to allow them to load
   */
  loadScripts() {
    setTimeout(() => {
      for (const scriptElement of this.querySelectorAll('script')) {
        const newScriptElement = document.createElement('script');
        for (const attribute of scriptElement.attributes) {
          newScriptElement.setAttribute(attribute.name, attribute.value);
        }
        scriptElement.parentNode.replaceChild(newScriptElement, scriptElement);
      }
    });
  }
}

customElements.get('dynamic-section') ||
  customElements.define('dynamic-section', DynamicSectionElement);

export default DynamicSectionElement;
